/**
 * First-login setup. The assistant drafts words. Nothing is saved until the admin edits and continues.
 * api is the admin API.json client: api(method, path, body).
 */
import { BusinessHoursSettings } from './business-hours.js';
import { ConsentFormSettings } from './consent-form-settings.js';
import { DncComplianceSettings } from './dnc-compliance-settings.js';
import { VoicemailDropSettings } from './voicemail-settings.js';

const { useEffect, useState } = React;

const STEPS = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'business', title: 'Your business' },
  { id: 'profile', title: 'Your assistant' },
  { id: 'hours', title: 'Hours' },
  { id: 'phone', title: 'Phone number' },
  { id: 'voice', title: 'Voice' },
  { id: 'compliance', title: 'Rules' },
  { id: 'finish', title: 'Review' }
];

const ZONES = [
  ['America/New_York', 'Eastern time'],
  ['America/Chicago', 'Central time'],
  ['America/Denver', 'Mountain time'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific time'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
  ['UTC', 'UTC']
];

const STEP_FOR = {
  business_profile: 'business',
  ai_profile: 'profile',
  opening_line: 'profile',
  inbound_script: 'profile',
  calling_hours: 'hours',
  phone_outbound: 'phone',
  phone_inbound: 'phone',
  voice: 'voice',
  legal_terms: 'compliance',
  consent: 'compliance',
  dnc: 'compliance',
  handoff: 'finish',
  voicemail: 'finish',
  plan: 'finish',
  workspace_active: 'finish',
  platform_calling: 'finish'
};

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function blankDraft() {
  return {
    business_name: '',
    industry_label: '',
    website: '',
    timezone: '',
    agent_name: '',
    summary: '',
    services: '',
    ideal_customer: '',
    pain_points: '',
    persona: '',
    opening_line: '',
    call_goal: '',
    script: '',
    objections: '',
    faqs: [],
    inbound_greeting: '',
    inbound_instructions: '',
    handoff_number: '',
    purpose: 'sales',
    tone: 'Warm and brief',
    description: ''
  };
}

function fromProfile(profile) {
  const src = profile || {};
  return {
    ...blankDraft(),
    business_name: src.business_name || '',
    industry_label: src.industry_label || '',
    website: src.website || '',
    timezone: src.timezone || '',
    agent_name: src.agent_name || '',
    summary: src.summary || '',
    services: src.services || '',
    ideal_customer: src.ideal_customer || '',
    pain_points: src.pain_points || '',
    persona: src.persona || '',
    opening_line: src.opening_line || '',
    call_goal: src.call_goal || '',
    script: src.script || '',
    objections: src.objections || '',
    faqs: Array.isArray(src.faqs) ? src.faqs : [],
    inbound_greeting: src.inbound_greeting || '',
    inbound_instructions: src.inbound_instructions || '',
    handoff_number: src.handoff_number || ''
  };
}

export function openSetupWizard() {
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('open-setup-wizard'));
  }
}

export function FinishSetupCard({ api, user }) {
  const [status, setStatus] = useState(null);
  const role = user && user.role;
  const tenantId = user && user.tenant_id;
  const allowed = role === 'tenant_admin' || (role === 'super_admin' && tenantId != null && tenantId !== '');

  useEffect(() => {
    if (!allowed || typeof api !== 'function') return undefined;
    let cancel = false;
    api('GET', '/api/onboarding/status').then((row) => {
      if (!cancel) setStatus(row);
    }).catch(() => { if (!cancel) setStatus(null); });
    return () => { cancel = true; };
  }, [allowed, api, user && user.id]);

  if (!allowed || !status || !status.ok) return null;
  if (status.ready_for_outbound && status.ready_for_inbound && status.completed_at) return null;
  const steps = status.steps || [];
  const blockers = steps.filter((item) => item.blocks_outbound && !item.done);
  const openItems = steps.filter((item) => item.required && !item.done && !item.blocks_outbound);
  return (
    <section className="card setup-finish-card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div>
          <div className="card-title">Finish setup</div>
          <div className="card-subtitle">
            {blockers.length
              ? 'Outgoing calls stay blocked until the red items are done.'
              : 'Outgoing calls can start. Open items below do not stop a call.'}
          </div>
        </div>
      </div>
      <div className="card-body">
        {blockers.length ? (
          <ul className="setup-list">
            {blockers.map((item) => (
              <li key={item.id}>
                <span className="setup-mark bad" aria-hidden="true">!</span>
                <div>
                  <div className="setup-item-title">{item.title}</div>
                  <p className="setup-item-copy">{item.blocking_reason || item.fix_hint}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {openItems.length ? (
          <ul className="setup-list">
            {openItems.map((item) => (
              <li key={item.id}>
                <span className="setup-mark warn" aria-hidden="true">!</span>
                <div>
                  <div className="setup-item-title">{item.title}</div>
                  <p className="setup-item-copy">{item.blocking_reason || item.fix_hint}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        {!blockers.length && !openItems.length ? (
          <p className="muted">The required pieces are in place. Open setup if you want to review them.</p>
        ) : null}
        <div className="setup-card-actions">
          <button type="button" className="button primary" onClick={openSetupWizard}>Open setup</button>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, span, hint }) {
  return (
    <div className={span ? 'field field-span' : 'field'}>
      <label>{label}</label>
      {children}
      {hint ? <p className="setup-help">{hint}</p> : null}
    </div>
  );
}

export function SetupWizard({ api, toast, user, open, onClose, startStep }) {
  const [stepId, setStepId] = useState(startStep || 'welcome');
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState(blankDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [legal, setLegal] = useState(null);
  const [checked, setChecked] = useState({});
  const [voice, setVoice] = useState('');
  const index = Math.max(0, STEPS.findIndex((item) => item.id === stepId));
  const step = STEPS[index] || STEPS[0];

  function setField(key, value) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function load() {
    const row = await api('GET', '/api/onboarding/status');
    setStatus(row);
    if (row && row.profile) setDraft((prev) => ({ ...prev, ...fromProfile(row.profile), purpose: prev.purpose, tone: prev.tone, description: prev.description }));
    if (row && row.voice) setVoice(row.voice.stored || '');
    if (!startStep && row && row.wizard_step && STEPS.some((item) => item.id === row.wizard_step)) {
      setStepId(row.wizard_step);
    }
    return row;
  }

  useEffect(() => {
    if (!open) return undefined;
    let cancel = false;
    setError('');
    load().catch((err) => { if (!cancel) setError(errorMessage(err, 'Could not load setup.')); });
    return () => { cancel = true; };
  }, [open]);

  useEffect(() => {
    if (!open || step.id !== 'compliance') return undefined;
    let cancel = false;
    api('GET', '/api/legal/status').then((row) => { if (!cancel) setLegal(row); }).catch(() => {});
    return () => { cancel = true; };
  }, [open, step.id]);

  if (!open) return null;

  async function save(body) {
    const row = await api('POST', '/api/onboarding/save', { ...body, wizard_step: step.id });
    setStatus(row);
    return row;
  }

  async function remember(nextId) {
    try {
      await api('POST', '/api/onboarding/progress', { step: nextId });
    } catch { /* the next save still has the fields */ }
  }

  async function go(nextIndex) {
    setError('');
    const dest = STEPS[nextIndex];
    if (!dest) return;
    setBusy(true);
    try {
      if (step.id === 'business') {
        if (!draft.business_name.trim()) throw new Error('Enter the business name.');
        if (!draft.timezone) throw new Error('Choose your time zone.');
        await save({
          business_name: draft.business_name,
          industry_label: draft.industry_label,
          website: draft.website,
          timezone: draft.timezone,
          wizard_step: dest.id
        });
      } else if (step.id === 'profile') {
        if (!draft.agent_name.trim() || !draft.services.trim() || !draft.call_goal.trim() || !draft.opening_line.trim()) {
          throw new Error('Add the assistant name, what you offer, the goal of a call, and the opening line.');
        }
        await save({
          agent_name: draft.agent_name,
          summary: draft.summary,
          persona: draft.persona,
          services: draft.services,
          ideal_customer: draft.ideal_customer,
          pain_points: draft.pain_points,
          call_goal: draft.call_goal,
          script: draft.script,
          objections: draft.objections,
          faqs: draft.faqs,
          opening_line: draft.opening_line,
          inbound_greeting: draft.inbound_greeting,
          inbound_instructions: draft.inbound_instructions,
          wizard_step: dest.id
        });
      } else if (step.id === 'voice') {
        await save({ inbound_voice_tier: voice || null, wizard_step: dest.id });
      } else if (step.id === 'hours' && draft.timezone) {
        await save({ timezone: draft.timezone, wizard_step: dest.id });
      } else {
        await remember(dest.id);
      }
      setStepId(dest.id);
    } catch (err) {
      setError(errorMessage(err, 'Could not save this step.'));
    } finally {
      setBusy(false);
    }
  }

  async function draftWithAi(section) {
    setError('');
    if (!draft.business_name.trim()) {
      setError('Enter the business name on the previous step, or type it here first.');
      return;
    }
    setBusy(true);
    try {
      const body = {
        business_name: draft.business_name,
        website: draft.website,
        description: draft.description || draft.summary,
        industry: draft.industry_label,
        purpose: draft.purpose,
        tone: draft.tone
      };
      if (section) body.section = section;
      const row = await api('POST', '/api/onboarding/ai-draft', body);
      const next = row && row.draft ? row.draft : {};
      setDraft((prev) => {
        const merged = { ...prev };
        if (next.business_summary) merged.summary = next.business_summary;
        if (next.services) merged.services = next.services;
        if (next.ideal_customer) merged.ideal_customer = next.ideal_customer;
        if (next.pain_points) merged.pain_points = next.pain_points;
        if (next.agent_name) merged.agent_name = next.agent_name;
        if (next.persona) merged.persona = next.persona;
        if (next.opening_line) merged.opening_line = next.opening_line;
        if (next.call_goal) merged.call_goal = next.call_goal;
        if (next.script) merged.script = next.script;
        if (next.objections) merged.objections = next.objections;
        if (Array.isArray(next.faqs) && next.faqs.length) merged.faqs = next.faqs;
        if (next.inbound_greeting) merged.inbound_greeting = next.inbound_greeting;
        if (next.inbound_instructions) merged.inbound_instructions = next.inbound_instructions;
        return merged;
      });
      if (toast) toast(section ? 'Updated that section. Read it before you continue.' : 'Draft ready. Read it and change anything that is wrong.');
    } catch (err) {
      setError(errorMessage(err, 'Could not draft this.'));
    } finally {
      setBusy(false);
    }
  }

  async function acceptTerms() {
    const docs = legal && Array.isArray(legal.docs) ? legal.docs : [];
    setBusy(true);
    setError('');
    try {
      const row = await api('POST', '/api/legal/accept', {
        agree: true,
        doc_keys: docs.map((doc) => doc.key)
      });
      setLegal(row);
      const fresh = await load();
      setStatus(fresh);
      if (toast) toast('Terms saved.');
    } catch (err) {
      setError(errorMessage(err, 'Could not save the terms.'));
    } finally {
      setBusy(false);
    }
  }

  async function skip() {
    setBusy(true);
    try {
      await api('POST', '/api/onboarding/progress', { dismiss: true, step: step.id });
    } catch { /* closing still leaves the checklist on the home page */ }
    setBusy(false);
    if (onClose) onClose();
  }

  async function finish() {
    setBusy(true);
    setError('');
    try {
      await api('POST', '/api/onboarding/progress', { complete: true, step: 'finish' });
      if (toast) toast('Setup saved. You can place a call from a lead.');
      if (onClose) onClose();
    } catch (err) {
      const data = err && err.data;
      if (data && data.steps) setStatus(data);
      setError(errorMessage(err, 'Setup still has open items.'));
      const first = data && Array.isArray(data.steps) && data.steps.find((item) => item.blocks_outbound && !item.done);
      if (first && STEP_FOR[first.id]) setStepId(STEP_FOR[first.id]);
    } finally {
      setBusy(false);
    }
  }

  function jump(id) {
    if (STEPS.some((item) => item.id === id)) setStepId(id);
  }

  const docs = legal && Array.isArray(legal.docs) ? legal.docs : [];
  const termsDone = docs.length > 0 && docs.every((doc) => doc.tenant_accepted);
  const allChecked = docs.length > 0 && docs.every((doc) => checked[doc.key]);
  const premiumAllowed = !!(status && status.voice && status.voice.premium_allowed);
  const missing = (status && status.steps || []).filter((item) => !item.done);

  return (
    <div className="setup-wizard" role="presentation" data-step={step.id}>
      <div
        className="setup-wizard-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="setup-wizard-title"
      >
        <div className="setup-wizard-head">
          <p className="setup-wizard-kicker">Setup · {index + 1} of {STEPS.length}</p>
          <h2 id="setup-wizard-title">{step.title}</h2>
          <div className="setup-wizard-progress">
            <div className="setup-wizard-track" aria-hidden="true">
              <div className="setup-wizard-fill" style={{ width: `${Math.round(((index + 1) / STEPS.length) * 100)}%` }} />
            </div>
            <span className="setup-help">{Math.round(((index + 1) / STEPS.length) * 100)}%</span>
          </div>
        </div>
        <div className="setup-wizard-body">
          {error ? <div className="setup-error" role="alert">{error}</div> : null}
          {step.id === 'welcome' ? (
            <div>
              <p>Welcome. This short setup tells the assistant who you are, when you call, and what it should say.</p>
              <p>You can leave and come back. Nothing is published until you save a step. The AI draft is only a starting point. You edit every line.</p>
              <p className="setup-help">Outgoing calls also need a phone number assigned by the platform, and your acceptance of the calling terms.</p>
            </div>
          ) : null}
          {step.id === 'business' ? (
            <div className="setup-wizard-grid">
              <Field label="Business name" span hint="This is the name people hear. Use the public name, not an internal nickname.">
                <input className="input" value={draft.business_name} maxLength={120} onChange={(e) => setField('business_name', e.target.value)} />
              </Field>
              <Field label="What kind of business">
                <input className="input" value={draft.industry_label} maxLength={80} placeholder="Roofing, dental, home services" onChange={(e) => setField('industry_label', e.target.value)} />
              </Field>
              <Field label="Website" hint="Optional. Used only to draft your profile. Nothing is published from the site automatically.">
                <input className="input" value={draft.website} maxLength={300} placeholder="https://example.com" onChange={(e) => setField('website', e.target.value)} />
              </Field>
              <Field label="Your time zone">
                <select className="input" value={draft.timezone} onChange={(e) => setField('timezone', e.target.value)}>
                  <option value="">Choose a time zone</option>
                  {ZONES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
            </div>
          ) : null}
          {step.id === 'profile' ? (
            <div>
              <div className="setup-wizard-grid">
                <Field label="What are the calls for?">
                  <select className="input" value={draft.purpose} onChange={(e) => setField('purpose', e.target.value)}>
                    <option value="sales">Sales</option>
                    <option value="appointments">Appointments</option>
                    <option value="follow-up">Follow-up</option>
                    <option value="reception">Answering the phone</option>
                  </select>
                </Field>
                <Field label="Tone">
                  <select className="input" value={draft.tone} onChange={(e) => setField('tone', e.target.value)}>
                    <option>Warm and brief</option>
                    <option>Calm and direct</option>
                    <option>Friendly and upbeat</option>
                  </select>
                </Field>
                <Field label="A short description" span hint="A few sentences about what you do. The draft uses this plus your website.">
                  <textarea className="input" rows={3} maxLength={2000} value={draft.description} onChange={(e) => setField('description', e.target.value)} />
                </Field>
              </div>
              <div className="setup-actions-inline">
                <button type="button" className="button primary" disabled={busy} onClick={() => draftWithAi()}>Draft with AI</button>
              </div>
              <p className="setup-help">The system already says it is an AI and that the call may be recorded, before the opening line. Do not write that again.</p>
              <ProfileFields draft={draft} setField={setField} busy={busy} onRegenerate={draftWithAi} />
            </div>
          ) : null}
          {step.id === 'hours' ? (
            <div>
              <p>Calls to other people only go out from 8am to 9pm in their local time, and until 8pm in Florida. You do not set that window. It is applied for you.</p>
              <Field label="Your time zone" hint="Used for follow-ups and for the hours you are open.">
                <select className="input" value={draft.timezone} onChange={(e) => setField('timezone', e.target.value)}>
                  <option value="">Choose a time zone</option>
                  {ZONES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <div className="setup-section">
                <h3>When you are open</h3>
                <p className="setup-help">These hours tell the assistant when your team is around. They do not override the 8am to 9pm calling limit.</p>
                <BusinessHoursSettings api={api} toast={toast} timezone={draft.timezone || 'America/Los_Angeles'} />
              </div>
            </div>
          ) : null}
          {step.id === 'phone' ? (
            <PhoneStep status={status} />
          ) : null}
          {step.id === 'voice' ? (
            <div>
              <p>This is the voice for incoming calls. Outgoing calls use the same kind of assistant.</p>
              <label className="setup-choice">
                <input type="radio" name="setup-voice" checked={voice === ''} onChange={() => setVoice('')} />
                <div>
                  <strong>Automatic</strong>
                  <span>Uses the richer voice when your plan includes it, and the standard voice otherwise.</span>
                </div>
              </label>
              <label className="setup-choice">
                <input type="radio" name="setup-voice" checked={voice === 'standard'} onChange={() => setVoice('standard')} />
                <div>
                  <strong>Standard</strong>
                  <span>The lighter voice. A good default for most calls.</span>
                </div>
              </label>
              <label className={premiumAllowed ? 'setup-choice' : 'setup-choice is-disabled'}>
                <input type="radio" name="setup-voice" disabled={!premiumAllowed} checked={voice === 'premium'} onChange={() => setVoice('premium')} />
                <div>
                  <strong>Premium</strong>
                  <span>{premiumAllowed ? 'The richer voice. Your plan includes it.' : 'Premium is not included in this plan. Choose Automatic or Standard.'}</span>
                </div>
              </label>
            </div>
          ) : null}
          {step.id === 'compliance' ? (
            <div>
              <div className="setup-section">
                <h3>Calling terms</h3>
                <p>Outgoing calls stay blocked until a workspace admin accepts these.</p>
                {docs.map((doc) => (
                  <label key={doc.key} className="setup-check-label" style={{ fontWeight: 500, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!checked[doc.key] || !!doc.tenant_accepted}
                      disabled={!!doc.tenant_accepted}
                      onChange={(e) => setChecked((prev) => ({ ...prev, [doc.key]: e.target.checked }))}
                    />
                    {' '}
                    <a href={doc.url} target="_blank" rel="noreferrer">{doc.title}</a>
                    {doc.tenant_accepted ? ' · accepted' : ''}
                  </label>
                ))}
                {termsDone ? <p className="setup-help">These terms are accepted.</p> : (
                  <button type="button" className="button primary" disabled={busy || !allChecked} onClick={acceptTerms}>I agree</button>
                )}
              </div>
              <div className="setup-section">
                <h3>Permission to call</h3>
                <p>The assistant can call someone only when that person has given written permission, or when the number is a business landline and the lead has a company name. A cell phone without that permission is not called.</p>
                <ConsentFormSettings api={api} toast={toast} />
              </div>
              <div className="setup-section">
                <h3>Do Not Call</h3>
                <p>{status && status.dnc ? status.dnc.message : 'People who asked you to stop, and numbers on your own list, are not called.'}</p>
                {status && status.dnc && status.dnc.san_required ? <DncComplianceSettings api={api} toast={toast} /> : null}
              </div>
            </div>
          ) : null}
          {step.id === 'finish' ? (
            <div>
              <p>Red items stop outgoing calls. That is a missing phone number, calling terms that are not accepted, a pause, or a required Do Not Call registration. An empty opening line does not stop a call. The platform already has a default line, and it still says the call is from an AI before that line.</p>
              <ul className="setup-list">
                {(status && status.steps ? status.steps : []).map((item) => {
                  const bad = item.blocks_outbound && !item.done;
                  const warn = !item.done && item.required && !item.blocks_outbound;
                  return (
                    <li key={item.id}>
                      <span className={item.done ? 'setup-mark ok' : bad ? 'setup-mark bad' : warn ? 'setup-mark warn' : 'setup-mark opt'} aria-hidden="true">
                        {item.done ? '✓' : bad || warn ? '!' : '○'}
                      </span>
                      <div>
                        <div className="setup-item-title">{item.title}</div>
                        <p className="setup-item-copy">{item.done ? item.fix_hint : (item.blocking_reason || item.fix_hint)}</p>
                      </div>
                      {!item.done ? (
                        <button type="button" className="button small outline" onClick={() => jump(STEP_FOR[item.id] || 'finish')}>Fix</button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              <div className="setup-section">
                <h3>If someone asks for a person</h3>
                <Field label="Transfer number" hint="Optional. Used when no one in the app can take the call.">
                  <input className="input" value={draft.handoff_number || ''} placeholder="+13105551234" onChange={(e) => setField('handoff_number', e.target.value)} />
                </Field>
                <button
                  type="button"
                  className="button outline"
                  disabled={busy}
                  onClick={() => save({ handoff_number: draft.handoff_number || '' }).then(() => toast && toast('Transfer number saved.')).catch((err) => setError(errorMessage(err, 'Could not save that number.')))}
                >
                  Save transfer number
                </button>
              </div>
              <div className="setup-section">
                <h3>Voicemail</h3>
                <VoicemailDropSettings api={api} toast={toast} />
              </div>
              <p className="setup-help">There is no practice call from this screen. When the red items are clear, open a lead and choose Start call. Call your own cell only if you have given written permission.</p>
              {missing.some((item) => item.blocks_outbound && !item.done) ? null : (
                <p>Outgoing setup looks complete. You can finish.</p>
              )}
            </div>
          ) : null}
        </div>
        <div className="setup-wizard-foot">
          <button type="button" className="button outline" disabled={busy} onClick={skip}>Skip for now</button>
          <div className="setup-nav">
            <button type="button" className="button outline" disabled={busy || index === 0} onClick={() => setStepId(STEPS[index - 1].id)}>Back</button>
            {step.id === 'finish' ? (
              <button type="button" className="button primary" disabled={busy} onClick={finish}>Finish</button>
            ) : (
              <button type="button" className="button primary" disabled={busy} onClick={() => go(index + 1)}>Next</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileFields({ draft, setField, busy, onRegenerate }) {
  const fields = [
    ['summary', 'Business summary', 'business_summary', true],
    ['services', 'What you offer', 'services', false],
    ['ideal_customer', 'Who you want to reach', 'ideal_customer', true],
    ['agent_name', 'Assistant name', 'agent_name', false],
    ['persona', 'How the assistant should sound', 'persona', true],
    ['opening_line', 'Opening line, after the AI notice', 'opening_line', true],
    ['call_goal', 'Goal of the call', 'call_goal', true],
    ['script', 'How the call should go', 'script', true],
    ['objections', 'If they push back', 'objections', true],
    ['inbound_greeting', 'Greeting when someone calls you', 'inbound_greeting', true],
    ['inbound_instructions', 'Instructions for incoming calls', 'inbound_instructions', true]
  ];
  return (
    <div>
      {fields.map(([key, label, section, area]) => (
        <div className="field" key={key}>
          <label>{label}</label>
          {area ? (
            <textarea className="input" rows={key === 'script' || key === 'inbound_instructions' ? 4 : 2} value={draft[key] || ''} onChange={(e) => setField(key, e.target.value)} />
          ) : (
            <input className="input" value={draft[key] || ''} onChange={(e) => setField(key, e.target.value)} />
          )}
          <div className="setup-actions-inline">
            <button type="button" className="button small outline" disabled={busy} onClick={() => onRegenerate(section)}>Regenerate</button>
          </div>
        </div>
      ))}
      <div className="field">
        <label>Common questions</label>
        {(draft.faqs || []).map((faq, i) => (
          <div key={i} className="setup-section">
            <input className="input" value={faq.question || ''} placeholder="Question" onChange={(e) => {
              const faqs = draft.faqs.slice();
              faqs[i] = { ...faqs[i], question: e.target.value };
              setField('faqs', faqs);
            }} />
            <textarea className="input" rows={2} value={faq.answer || ''} placeholder="Answer" style={{ marginTop: 6 }} onChange={(e) => {
              const faqs = draft.faqs.slice();
              faqs[i] = { ...faqs[i], answer: e.target.value };
              setField('faqs', faqs);
            }} />
          </div>
        ))}
        <button type="button" className="button small outline" disabled={busy} onClick={() => onRegenerate('faqs')}>Regenerate questions</button>
      </div>
    </div>
  );
}

function PhoneStep({ status }) {
  const phone = status && status.phone;
  const outbound = phone && phone.outbound_assigned;
  const inbound = phone && phone.inbound_assigned;
  return (
    <div>
      <div className="setup-wait">
        <strong>{outbound && inbound ? 'Your numbers are connected.' : 'Waiting for your phone number.'}</strong>
        <p>A platform admin assigns the number. You do not buy one in this setup. This step updates on its own when the number is on the workspace.</p>
      </div>
      <ul className="setup-list">
        <li>
          <span className={outbound ? 'setup-mark ok' : 'setup-mark bad'}>{outbound ? '✓' : '!'}</span>
          <div>
            <div className="setup-item-title">Number people see when you call</div>
            <p className="setup-item-copy">{outbound ? phone.outbound_display : 'Not assigned yet.'}</p>
          </div>
        </li>
        <li>
          <span className={inbound ? 'setup-mark ok' : 'setup-mark bad'}>{inbound ? '✓' : '!'}</span>
          <div>
            <div className="setup-item-title">Number people dial to reach you</div>
            <p className="setup-item-copy">{inbound ? 'Connected.' : 'Not connected yet.'}</p>
          </div>
        </li>
      </ul>
    </div>
  );
}

export function setupBlockMessage(status) {
  const missing = (status && status.steps || []).filter((item) => item.blocks_outbound && !item.done);
  if (!missing.length) return '';
  const titles = missing.map((item) => item.title).join(', ');
  return `This workspace is not ready for AI calls yet. Still needed: ${titles}. Open Finish setup to fix each one.`;
}
